/**
 * Proactive offers — "Argo asks where it can help."
 *
 * The mandate: Viktor does research, reminds you of things, and asks where
 * it can help. This PURE engine turns live records into up to two grounded
 * *offers* — a research offer and a reminder offer — each wired to a REAL
 * existing mechanism so an accepted offer can never dead-end:
 *
 *   • RESEARCH — a prospect carrying a real deal (≥ $20k) that has NOT been
 *     researched in the last 30 days. The offer starts the EXISTING prospect
 *     enrichment/research run (src/lib/agent/enrich.ts → enrichProspect,
 *     persisted via updatePerson — the exact mechanism the People view's
 *     "Enrich" button fires). That run needs the remote Claude/enrich worker;
 *     when it is UNCONFIGURED the offer does not pretend it can act — it
 *     downgrades to an honest note ("research runs when the Claude worker is
 *     connected") and carries NO action, so a click can never hit a dead end.
 *
 *   • REMINDER — a real deadline (a prospect follow-up date or a customer
 *     renewal) landing within 7 days that has NO follow-up already scheduled.
 *     The offer queues the EXISTING `schedule_follow_up` proposal through the
 *     governed approvals lane (queueApproval → Dashboard inbox), exactly like
 *     every other agent write.
 *
 * PURE by contract: reads only its arguments, no store, no network, no clock
 * unless `now` is omitted, no input mutation. A clean book (or nothing worth
 * offering) yields an empty list — Argo never manufactures a reason to speak.
 * Dismissal (hide-for-7-days) and the actual firing live in the wiring layer
 * (offersDismissal.ts + the dock), never here.
 */

import type { Person } from '../db/types';
import type { ScheduleFollowUpAction } from './types';

const DAY_MS = 86_400_000;

/** How stale a research read may be before we re-offer (days). */
export const RESEARCH_STALE_DAYS = 30;
/** How near a deadline must be to earn a reminder offer (days). */
export const REMINDER_WINDOW_DAYS = 7;
/** Minimum deal size for a prospect to earn a research offer (USD). */
export const RESEARCH_MIN_DEAL = 20_000;

/**
 * What accepting an offer actually fires. Descriptor-only so the engine stays
 * pure — the wiring layer maps each onto the real mechanism:
 *
 *   • research → enrichProspect(person) + updatePerson (People view's run).
 *   • reminder → queueApproval(schedule_follow_up).
 */
export type OfferAction =
  | { type: 'research'; personId: string; personName: string }
  | { type: 'reminder'; action: ScheduleFollowUpAction };

export type OfferKind = 'research' | 'reminder';

/** One grounded offer, ready to render as a chip. */
export interface Offer {
  /** Stable id: `offer:<kind>:<entityId>` — the dismissal key. */
  id: string;
  kind: OfferKind;
  /** The record this offer concerns — lets the dock deep-link. */
  entityId: string;
  personId: string;
  /** The offer phrased as a question, e.g. "Want me to research Acme…?" */
  text: string;
  /** The honest "why" line, drawn strictly from record data. */
  evidence: string;
  /**
   * The real mechanism accepting fires — null ONLY when the offer is gated
   * (research worker unconfigured), in which case `text` states so honestly
   * and the chip renders as a note, not an actionable button.
   */
  action: OfferAction | null;
  /** True when the offer is an honest "can't act yet" note (no `action`). */
  gated?: boolean;
}

export interface ComposeOffersInput {
  people?: Person[];
  /**
   * Whether the research/enrich worker is connected (enrich.isConfigured()).
   * When false, research offers downgrade to an honest gated note instead of
   * appearing as an actionable button that would dead-end.
   */
  researchConfigured?: boolean;
  /** Injectable clock for deterministic tests; defaults to now. */
  now?: number;
}

/** Weekday name for a plain date, e.g. "Monday". */
function weekday(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return 'soon';
  return new Date(t).toLocaleDateString('en-US', { weekday: 'long', timeZone: 'UTC' });
}

/** Whole days from `now` until `iso` (negative = already past). */
function daysUntil(iso: string, now: number): number {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return NaN;
  return Math.ceil((t - now) / DAY_MS);
}

function usd(n: number): string {
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
}

/** Human label for a person's company (falls back to the name). */
function companyOf(p: Person): string {
  return p.company?.trim() || p.name;
}

// ---------------------------------------------------------------------------
// RESEARCH offers — grounded in a real deal + a stale/absent research read.
// ---------------------------------------------------------------------------

/** True when a prospect has NO research read, or it's older than the window. */
function researchIsStale(p: Person, now: number): boolean {
  const at = p.enrichment?.evaluatedAt;
  if (!at) return true; // never researched
  const t = Date.parse(at);
  if (Number.isNaN(t)) return true;
  return now - t > RESEARCH_STALE_DAYS * DAY_MS;
}

/** The single best research offer, or null when nothing qualifies. */
function bestResearchOffer(
  people: Person[],
  configured: boolean,
  now: number,
): Offer | null {
  const candidates = people
    .filter(
      (p) =>
        p.category === 'prospect' &&
        p.stage !== 'closed-won' &&
        p.stage !== 'closed-lost' &&
        (p.estimatedValue ?? 0) >= RESEARCH_MIN_DEAL &&
        researchIsStale(p, now),
    )
    // Biggest deal first — research the dollars most at stake before a touch.
    .sort((a, b) => (b.estimatedValue ?? 0) - (a.estimatedValue ?? 0));

  const p = candidates[0];
  if (!p) return null;

  const company = companyOf(p);
  const value = p.estimatedValue ?? 0;
  const never = !p.enrichment?.evaluatedAt;
  const evidence = `${usd(value)} deal · ${
    never ? 'never researched' : `last research ${daysAgoLabel(p.enrichment!.evaluatedAt!, now)}`
  }`;

  if (!configured) {
    // Honest: don't offer a button that would dead-end. Say why, act never.
    return {
      id: `offer:research:${p.id}`,
      kind: 'research',
      entityId: p.id,
      personId: p.id,
      text: `Research runs when the Claude worker is connected — then I can brief you on ${company}.`,
      evidence,
      action: null,
      gated: true,
    };
  }

  return {
    id: `offer:research:${p.id}`,
    kind: 'research',
    entityId: p.id,
    personId: p.id,
    text: `Want me to research ${company} before your next touch?`,
    evidence,
    action: { type: 'research', personId: p.id, personName: p.name },
  };
}

/** "12d ago" style label for a past ISO timestamp. */
function daysAgoLabel(iso: string, now: number): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return 'unknown';
  const days = Math.max(0, Math.floor((now - t) / DAY_MS));
  return `${days}d ago`;
}

// ---------------------------------------------------------------------------
// REMINDER offers — grounded in a real deadline within 7 days, unscheduled.
// ---------------------------------------------------------------------------

/** True when a live relationship already has a follow-up on the books. */
function hasFollowUpScheduled(p: Person, now: number): boolean {
  if (!p.followUpDate) return false;
  const t = Date.parse(p.followUpDate);
  // A follow-up that's still in the future (or today) counts as scheduled.
  return !Number.isNaN(t) && t + DAY_MS > now;
}

/** The single best reminder offer, or null when nothing qualifies. */
function bestReminderOffer(people: Person[], now: number): Offer | null {
  let best: { offer: Offer; daysOut: number } | null = null;

  for (const p of people) {
    if (p.status === 'churned' || p.stage === 'closed-lost') continue;

    // The real deadline: a customer renewal or a prospect follow-up date.
    // The already-scheduled guard applies ONLY to renewals — for a prospect
    // the follow-up date IS the thing worth a reminder ("Remind you Thursday
    // about Jane — send pricing?"), not a reason to stay silent.
    const deadline =
      p.category === 'customer' ? p.renewalDate : p.followUpDate ?? p.renewalDate;
    if (p.category === 'customer' && hasFollowUpScheduled(p, now)) continue;
    if (!deadline) continue;
    const daysOut = daysUntil(deadline, now);
    if (Number.isNaN(daysOut) || daysOut < 0 || daysOut > REMINDER_WINDOW_DAYS) continue;

    const thing =
      p.category === 'customer'
        ? `${companyOf(p)}'s renewal`
        : `${p.name}${p.nextStep ? ` — ${p.nextStep}` : ''}`;
    const action: ScheduleFollowUpAction = {
      kind: 'schedule_follow_up',
      personId: p.id,
      personName: p.name,
      followUpDate: deadline,
      note:
        p.nextStep ??
        (p.category === 'customer' ? `Renewal due ${deadline}` : `Follow up — due ${deadline}`),
    };
    const offer: Offer = {
      id: `offer:reminder:${p.id}`,
      kind: 'reminder',
      entityId: p.id,
      personId: p.id,
      text: `Remind you ${weekday(deadline)} about ${thing}?`,
      evidence: `Due ${deadline}${
        p.category === 'customer' && p.accountValue
          ? ` · ${usd(p.accountValue)} ARR`
          : p.estimatedValue
            ? ` · ${usd(p.estimatedValue)} deal`
            : ''
      } · no follow-up scheduled`,
      action: { type: 'reminder', action },
    };

    // Soonest deadline wins — the one bearing down hardest.
    if (!best || daysOut < best.daysOut) best = { offer, daysOut };
  }

  return best?.offer ?? null;
}

// ---------------------------------------------------------------------------
// The engine
// ---------------------------------------------------------------------------

/**
 * Compose up to two grounded offers — one research, one reminder — from the
 * live people snapshot. PURE. Returns [] when nothing qualifies; the research
 * slot downgrades to an honest gated note when the worker is unconfigured.
 */
export function composeOffers(input: ComposeOffersInput): Offer[] {
  const now = input.now ?? Date.now();
  const people = input.people ?? [];
  const configured = input.researchConfigured ?? false;

  const offers: Offer[] = [];
  const research = bestResearchOffer(people, configured, now);
  if (research) offers.push(research);
  const reminder = bestReminderOffer(people, now);
  if (reminder) offers.push(reminder);

  // Cap at 2 (already at most 2, but explicit is honest).
  return offers.slice(0, 2);
}
